import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics';

/**
 * PersonalizationContext - Advanced personalization system
 * 
 * Features:
 * - User preference management
 * - Behavioral tracking and adaptation
 * - Content personalization
 * - A/B testing integration
 * - Machine learning insights
 */

// Personalization state structure
const initialState = {
  // User preferences
  preferences: {
    theme: 'system', // 'light', 'dark', 'system'
    language: 'en',
    industry: null,
    companySize: null,
    role: null,
    interests: [],
    experienceLevel: null,
    goals: [],
    communicationPreference: 'email',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    accessibility: {
      reducedMotion: false,
      highContrast: false,
      fontSize: 'medium',
      screenReader: false
    }
  },
  
  // Behavioral data
  behavior: {
    sessionCount: 0,
    totalTimeSpent: 0,
    pagesVisited: [],
    actionsTaken: [],
    conversionFunnel: 'awareness', // awareness, interest, consideration, intent, purchase
    engagementScore: 0,
    lastVisit: null,
    deviceType: 'desktop',
    browserType: 'chrome',
    location: null,
    referrer: null
  },
  
  // Content personalization
  content: {
    personalizedHeadlines: {},
    recommendedContent: [],
    customCTAs: {},
    dynamicPricing: null,
    relevantCaseStudies: [],
    industryInsights: [],
    personalizedTestimonials: []
  },
  
  // A/B testing
  experiments: {
    activeExperiments: {},
    variantAssignments: {},
    experimentHistory: []
  },
  
  // Machine learning insights
  insights: {
    predictedInterests: [],
    recommendedActions: [],
    riskScore: 0,
    engagementProbability: 0,
    conversionProbability: 0,
    nextBestAction: null
  },
  
  // System state
  isInitialized: false,
  isLoading: false,
  lastUpdated: null
};

// Action types
const PERSONALIZATION_ACTIONS = {
  INITIALIZE: 'INITIALIZE',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  TRACK_BEHAVIOR: 'TRACK_BEHAVIOR',
  UPDATE_CONTENT: 'UPDATE_CONTENT',
  ASSIGN_EXPERIMENT: 'ASSIGN_EXPERIMENT',
  UPDATE_INSIGHTS: 'UPDATE_INSIGHTS',
  SET_LOADING: 'SET_LOADING',
  RESET: 'RESET'
};

// Reducer function
const personalizationReducer = (state, action) => {
  switch (action.type) {
    case PERSONALIZATION_ACTIONS.INITIALIZE:
      return {
        ...state,
        ...action.payload,
        isInitialized: true,
        lastUpdated: new Date().toISOString()
      };
      
    case PERSONALIZATION_ACTIONS.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload
        },
        lastUpdated: new Date().toISOString()
      };
      
    case PERSONALIZATION_ACTIONS.TRACK_BEHAVIOR:
      return {
        ...state,
        behavior: {
          ...state.behavior,
          ...action.payload,
          lastVisit: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      };
      
    case PERSONALIZATION_ACTIONS.UPDATE_CONTENT:
      return {
        ...state,
        content: {
          ...state.content,
          ...action.payload
        },
        lastUpdated: new Date().toISOString()
      };
      
    case PERSONALIZATION_ACTIONS.ASSIGN_EXPERIMENT:
      return {
        ...state,
        experiments: {
          ...state.experiments,
          activeExperiments: {
            ...state.experiments.activeExperiments,
            [action.payload.experimentId]: action.payload.variant
          },
          variantAssignments: {
            ...state.experiments.variantAssignments,
            [action.payload.experimentId]: action.payload.variant
          }
        },
        lastUpdated: new Date().toISOString()
      };
      
    case PERSONALIZATION_ACTIONS.UPDATE_INSIGHTS:
      return {
        ...state,
        insights: {
          ...state.insights,
          ...action.payload
        },
        lastUpdated: new Date().toISOString()
      };
      
    case PERSONALIZATION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
      
    case PERSONALIZATION_ACTIONS.RESET:
      return {
        ...initialState,
        isInitialized: true
      };
      
    default:
      return state;
  }
};

// Context creation
const PersonalizationContext = createContext();

// Custom hook for using personalization context
export const usePersonalization = () => {
  const context = useContext(PersonalizationContext);
  if (!context) {
    throw new Error('usePersonalization must be used within a PersonalizationProvider');
  }
  return context;
};

// Personalization provider component
export const PersonalizationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(personalizationReducer, initialState);
  const { trackEvent } = useGoogleAnalytics();

  // Initialize personalization on mount
  useEffect(() => {
    initializePersonalization();
  }, []);

  // Initialize personalization system
  const initializePersonalization = async () => {
    dispatch({ type: PERSONALIZATION_ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Load saved preferences from localStorage
      const savedPreferences = localStorage.getItem('personalization_preferences');
      const savedBehavior = localStorage.getItem('personalization_behavior');
      const savedExperiments = localStorage.getItem('personalization_experiments');
      
      // Detect user device and browser
      const deviceType = detectDeviceType();
      const browserType = detectBrowserType();
      const location = await detectLocation();
      
      // Initialize with saved data or defaults
      const initialData = {
        preferences: savedPreferences ? JSON.parse(savedPreferences) : state.preferences,
        behavior: {
          ...state.behavior,
          ...(savedBehavior ? JSON.parse(savedBehavior) : {}),
          deviceType,
          browserType,
          location,
          sessionCount: (savedBehavior ? JSON.parse(savedBehavior).sessionCount : 0) + 1,
          lastVisit: new Date().toISOString()
        },
        experiments: savedExperiments ? JSON.parse(savedExperiments) : state.experiments
      };
      
      dispatch({ type: PERSONALIZATION_ACTIONS.INITIALIZE, payload: initialData });
      
      // Track initialization
      trackEvent('personalization_initialized', {
        device_type: deviceType,
        browser_type: browserType,
        has_saved_preferences: !!savedPreferences
      });
      
    } catch (error) {
      console.error('Failed to initialize personalization:', error);
      dispatch({ type: PERSONALIZATION_ACTIONS.INITIALIZE, payload: state });
    } finally {
      dispatch({ type: PERSONALIZATION_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Update user preferences
  const updatePreferences = (newPreferences) => {
    const updatedPreferences = { ...state.preferences, ...newPreferences };
    dispatch({ type: PERSONALIZATION_ACTIONS.UPDATE_PREFERENCES, payload: newPreferences });
    
    // Save to localStorage
    localStorage.setItem('personalization_preferences', JSON.stringify(updatedPreferences));
    
    // Track preference update
    trackEvent('preferences_updated', {
      preferences: Object.keys(newPreferences),
      total_preferences: Object.keys(updatedPreferences).length
    });
  };

  // Track user behavior
  const trackBehavior = (behaviorData) => {
    const newBehavior = {
      ...behaviorData,
      timestamp: new Date().toISOString()
    };
    
    dispatch({ type: PERSONALIZATION_ACTIONS.TRACK_BEHAVIOR, payload: newBehavior });
    
    // Update behavior in localStorage
    const currentBehavior = { ...state.behavior, ...newBehavior };
    localStorage.setItem('personalization_behavior', JSON.stringify(currentBehavior));
    
    // Track behavior event
    trackEvent('behavior_tracked', {
      behavior_type: behaviorData.type || 'general',
      data: behaviorData
    });
  };

  // Update personalized content
  const updateContent = (contentData) => {
    dispatch({ type: PERSONALIZATION_ACTIONS.UPDATE_CONTENT, payload: contentData });
    
    // Track content update
    trackEvent('content_updated', {
      content_types: Object.keys(contentData),
      personalization_level: 'high'
    });
  };

  // Assign user to A/B test experiment
  const assignExperiment = (experimentId, variants) => {
    // Simple hash-based assignment for consistency
    const userId = localStorage.getItem('user_id') || 'anonymous';
    const hash = hashString(userId + experimentId);
    const variantIndex = hash % variants.length;
    const variant = variants[variantIndex];
    
    dispatch({
      type: PERSONALIZATION_ACTIONS.ASSIGN_EXPERIMENT,
      payload: { experimentId, variant }
    });
    
    // Save experiment assignment
    const currentExperiments = { ...state.experiments };
    currentExperiments.activeExperiments[experimentId] = variant;
    currentExperiments.variantAssignments[experimentId] = variant;
    localStorage.setItem('personalization_experiments', JSON.stringify(currentExperiments));
    
    // Track experiment assignment
    trackEvent('experiment_assigned', {
      experiment_id: experimentId,
      variant: variant,
      user_id: userId
    });
  };

  // Update machine learning insights
  const updateInsights = (insights) => {
    dispatch({ type: PERSONALIZATION_ACTIONS.UPDATE_INSIGHTS, payload: insights });
    
    // Track insights update
    trackEvent('insights_updated', {
      insight_types: Object.keys(insights),
      confidence_score: insights.confidence || 0
    });
  };

  // Get personalized content
  const getPersonalizedContent = (contentType, fallback) => {
    return state.content[contentType] || fallback;
  };

  // Get user's experiment variant
  const getExperimentVariant = (experimentId) => {
    return state.experiments.variantAssignments[experimentId] || 'control';
  };

  // Check if user is in experiment
  const isInExperiment = (experimentId) => {
    return experimentId in state.experiments.activeExperiments;
  };

  // Get personalized recommendations
  const getRecommendations = (type = 'general') => {
    const recommendations = state.insights.recommendedActions || [];
    return recommendations.filter(rec => rec.type === type || type === 'general');
  };

  // Reset personalization data
  const resetPersonalization = () => {
    dispatch({ type: PERSONALIZATION_ACTIONS.RESET });
    localStorage.removeItem('personalization_preferences');
    localStorage.removeItem('personalization_behavior');
    localStorage.removeItem('personalization_experiments');
    
    trackEvent('personalization_reset', {
      reason: 'user_request'
    });
  };

  // Utility functions
  const detectDeviceType = () => {
    const userAgent = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) return 'mobile';
    return 'desktop';
  };

  const detectBrowserType = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'chrome';
    if (userAgent.includes('Firefox')) return 'firefox';
    if (userAgent.includes('Safari')) return 'safari';
    if (userAgent.includes('Edge')) return 'edge';
    return 'other';
  };

  const detectLocation = async () => {
    // Client-side IP geolocation (ipapi.co) is CORS-blocked from the browser and
    // failed on every page load, spamming the console while always resolving to
    // null. Disabled until a same-origin/server-proxied geolocation endpoint
    // exists. Returning null keeps the existing behavior without the failed request.
    return null;
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  };

  const contextValue = {
    // State
    ...state,
    
    // Actions
    updatePreferences,
    trackBehavior,
    updateContent,
    assignExperiment,
    updateInsights,
    getPersonalizedContent,
    getExperimentVariant,
    isInExperiment,
    getRecommendations,
    resetPersonalization
  };

  return (
    <PersonalizationContext.Provider value={contextValue}>
      {children}
    </PersonalizationContext.Provider>
  );
};

export default PersonalizationContext;
